import { useEffect, useId, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useLang } from '../i18n'
import { api, type BidJob } from '../lib/api'
import { bidJobsKey, jobKindWords, jobNoteWords, jobsPollMs, jobStatusText, jobStatusWords, jobSubject } from '../lib/bid-jobs'
import type { Words } from '../lib/slide-import'
import { JobDetailsDialog } from './JobDetailsDialog'

const fill = (s: string, words: Pick<Words, 'vars'>): string =>
  Object.entries(words.vars ?? {}).reduce((acc, [k, v]) => acc.replaceAll(`{${k}}`, () => String(v)), s)

/**
 * The bid home's Jobs section (AB#462): the last server-side jobs this person may see, newest first
 * — imports and indexing runs carry on without the member, who may close the dialog or leave, and
 * this is where they find out how they ended. What is listed is the backend's rule
 * (`GET /v1/bids/:bid/jobs`): a member's own jobs, every job of the bid for an admin.
 *
 * It reads the list by itself, every few seconds while a job runs and more slowly otherwise. The
 * table is gated on `isFetched` and keeps its last rows, so a refresh changes the words in a row and
 * nothing else (the same reasoning as the artefact grid's comment in BidHome.tsx), and a stalled
 * poll stays silent. The status is text, its percentage in the text; only a change of status is
 * announced, never a refresh or a percentage.
 */
export function JobsSection({ bid, onOpenPart }: { bid: string; onOpenPart: (room: string) => void }) {
  const { t } = useLang()
  const uid = useId()
  const read = useQuery({
    queryKey: bidJobsKey(bid),
    queryFn: () => api.bidJobs(bid),
    refetchInterval: (q) => jobsPollMs(q.state.data?.jobs ?? []),
  })
  const jobs = read.data?.jobs
  const [details, setDetails] = useState<BidJob | null>(null)

  const say = (value: string | Words | null): string => (value === null ? '' : typeof value === 'string' ? value : fill(t(value.key), value))
  const nameOf = (job: BidJob): string => `${t(jobKindWords(job.kind))} ${say(jobSubject(job))}`.trim()

  const [announced, setAnnounced] = useState('')
  const statuses = useRef<Map<string, BidJob['status']> | null>(null)
  useEffect(() => {
    if (!jobs) return
    const before = statuses.current
    statuses.current = new Map(jobs.map((j) => [j.id, j.status]))
    // The first read announces nothing: the rows are the page's content, not news.
    if (!before) return
    const changed = jobs.filter((j) => before.get(j.id) !== j.status)
    if (changed.length > 0) setAnnounced(changed.map((j) => `${nameOf(j)}: ${t(jobStatusWords(j.status))}`).join('. '))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a new language is no change of status
  }, [jobs])

  return (
    <section className="jobs" aria-labelledby={`${uid}-title`}>
      <h2 id={`${uid}-title`} className="jobs-title">
        {t('jobs_title')}
      </h2>
      {!read.isFetched && !jobs && <p className="jobs-empty">{t('loading')}</p>}
      {read.isFetched && !jobs && <p className="jobs-empty">{t('jobs_failed')}</p>}
      {jobs?.length === 0 && <p className="jobs-empty">{t('jobs_empty')}</p>}
      {jobs && jobs.length > 0 && (
        <table className="kbtable jobs-table">
          <caption className="visually-hidden">{t('jobs_caption')}</caption>
          <thead>
            <tr>
              <th scope="col">{t('jobs_col_job')}</th>
              <th scope="col">{t('jobs_col_member')}</th>
              <th scope="col">{t('jobs_col_status')}</th>
              <th scope="col">{t('jobs_col_details')}</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((job) => {
              const subject = say(jobSubject(job))
              return (
                <tr key={job.id}>
                  <th scope="row" data-label={t('jobs_col_job')}>
                    <span className="jobs-kind">{t(jobKindWords(job.kind))}</span>
                    {subject && <span className="jobs-subject">{subject}</span>}
                  </th>
                  <td data-label={t('jobs_col_member')}>{job.launchedBy.displayName ?? t('jobs_member_unknown')}</td>
                  <td data-label={t('jobs_col_status')}>
                    <span className={`jobs-status ${job.status}`}>{say(jobStatusText(job))}</span>
                    {job.note && <span className="jobs-note">{t(jobNoteWords(job.note))}</span>}
                  </td>
                  <td data-label={t('jobs_col_details')}>
                    <button
                      type="button"
                      className="jobs-details"
                      aria-label={fill(t('jobs_details_of'), { vars: { job: nameOf(job) } })}
                      onClick={() => setDetails(job)}
                    >
                      {t('jobs_details')}
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
      <p className="visually-hidden" role="status">
        {announced}
      </p>
      {details && (
        // The row keeps it up to date; a job pushed out of the list meanwhile stays as it was last seen.
        <JobDetailsDialog
          bid={bid}
          job={jobs?.find((j) => j.id === details.id) ?? details}
          onOpenPart={onOpenPart}
          onClose={() => setDetails(null)}
        />
      )}
    </section>
  )
}
